/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React from 'react';

import { TIVO_SERVICES } from '../../../config';
import { ImageAssetsService } from '../../../core/infrastructure';
import { ServicesClientImpl } from '../../../core/interactors';
import { PreviewComponent, PreviewComponentAssets } from '../../../core/usecases';
import {
    Badge,
    ChannelAiringInfo,
    ContentDescription,
    ContentTitle,
    Credits,
    Duration,
    FirstAirsOrAired,
    Genres,
    ParentalControlRating,
    Separator,
    ShowcaseBanner,
    StatusMessage,
    TimeAiringInfo,
} from '../ui-kit';
import { VodPreviewFormatter } from '../VodPreview/VodPreviewFormatter';
import { VodScreenType } from '../VodPreview/VodPreviewProps';
import type { TInfoCardContentUtil } from './InfoCardProps';

/**
 * NonEpisodicInfoCard component
 * Ref.: http://w3-engr/MyWorld/UserInterface/Hydra/Specs/STB/features/smart_tv_app/specific_behavior_changes/content.html#info_cards
 * UX Ref. https://app.zeplin.io/project/6266f85e6716281480fdbbd8/screen/68c3da4cf651b17caccd3c2b
 */
export const getNonEpisodicInfoCardContent: TInfoCardContentUtil = (metadata) => {
    const assetService = new ImageAssetsService({
        imageHostUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
    });
    const url: string = assetService.getEpisodeBannerImageUrlForVodContent(
        metadata.contentId || '',
    );
    const pcRatingNames = PreviewComponent.getParentalRatingNames(
        metadata?.internalRatingList || [],
    );
    const oPreviewAssets = new PreviewComponentAssets();
    const sContentDescription =
        metadata.description ||
        oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.DESCRIPTION_NOT_AVAILABLE,
        );
    const vodPreviewFormatter = new VodPreviewFormatter(metadata, VodScreenType.VOD_INFO_CARD);
    const entitlementAndAvailabilityStatus = vodPreviewFormatter.getCombinedStatusMessage();

    const getBodyContent = () => {
        return (
            <React.Fragment>
                {!entitlementAndAvailabilityStatus && (
                    <>
                        {metadata.channel && (
                            <ChannelAiringInfo
                                channelNumber={metadata.channel.channelNumber}
                                callSign={metadata.channel.callSign}
                                videoResolution={
                                    metadata.channel.videoResolution || metadata.videoResolution
                                }
                                isThreeD={metadata.isThreeD}
                            />
                        )}
                        {metadata.channel && metadata.startTime && metadata.endTime && (
                            <Separator />
                        )}
                        {metadata.startTime && metadata.endTime && (
                            <TimeAiringInfo
                                startTime={metadata.startTime}
                                endTime={metadata.endTime}
                            />
                        )}
                    </>
                )}
                <StatusMessage
                    entitlementAndAvailabilityStatus={entitlementAndAvailabilityStatus}
                    status={metadata.recordingStatus}
                    isKeepForeverRecording={metadata.isKeepForeverRecording}
                    showType={metadata.previewType}
                    isPartialRecording={metadata.isPartialRecording}
                    startTime={metadata.startTime}
                    endTime={metadata.endTime}
                    duration={metadata.recordingDuration}
                    recordingAvailability={metadata.recordingAvailability}
                />
                <ContentTitle title={metadata.title} />
                {entitlementAndAvailabilityStatus && (
                    <Badge
                        text={metadata.videoResolution || metadata.channel?.videoResolution}
                        uppercase={true}
                    />
                )}
                <Badge text={metadata.isNew ? 'New' : ''} />
                <ParentalControlRating ratingNames={pcRatingNames} />
                {entitlementAndAvailabilityStatus &&
                    metadata.videoResolution &&
                    metadata.isNew &&
                    pcRatingNames.length > 0 && <Separator />}
                <Genres categoryLabel={metadata.categoryLabel} />
                {metadata.categoryLabel && <Separator />}
                <Duration duration={metadata.duration} />
                {metadata.duration && <Separator />}
                <FirstAirsOrAired firstAiredDate={metadata.firstAiredDate} />
                <Credits creditString={metadata.creditString} />
                <ContentDescription
                    description={sContentDescription}
                    className="primary-text-style text-light mt-16"
                />
            </React.Fragment>
        );
    };

    return {
        headerContent: (
            <ShowcaseBanner
                bannerUrl={url || metadata.episodePreviewImageUrl || ''}
                fallbackText={metadata.title || ''}
            />
        ),
        headerClassName: 'banner-only',
        bodyContent: getBodyContent(),
    };
};
